/* eslint-disable no-unused-vars */

import React, { useState, useMemo, useEffect } from 'react';
import { store } from '@redux/store';

import style from '../../workorder.module.scss';
import SectionContainer from '@components/section-container';
import Icon from '@components/icon-component';
import AddEditCMLModal from './add-edit-cml-modal';
import useWindowDimensions from '@hooks/useWindowDimensions';
import Collapseable from '@components/collapseable';
import DataSnippets from '@components/data-snippet';
import _ from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { sortCMLList } from './helper';
import DisabledLocation from '@components/location-toast';

const CMLSubsection = ({
  dropdownMenuMapList,
  setValue,
  name,
  errors,
  baseName,
  fieldId,
  getValues,
  appliesToDataEquipmentId
}) => {
  const [searchParams] = useSearchParams();

  const equipmentId = searchParams.get('equipmentId') || '';

  const [openAddNewCMlModal, setOpenAddNewCMLModal] = useState(false);
  const [editCMLId, setEditCMLId] = useState(null);
  const [headerWidth, setHeaderWidth] = useState('');
  const [newCMLAdded, setNewCMLAdded] = useState(false);
  const [newReadingAdded, setNewReadingAdded] = useState(false);

  useEffect(() => {
    return () => {
      setEditCMLId(null);
    };
  }, []);

  const { picklists, cache } = store.getState();
  const { picklistsMap = {} } = picklists;

  const shellTminAndHeadTminValue = useMemo(() => {
    const selectedEquipment = equipmentId || appliesToDataEquipmentId || null;
    let equipment = cache?.equipments?.find((equ) => equ?.equipmentId === selectedEquipment);
    return {
      shellTmin: equipment?.shellTmin || 0,
      shellTminUnit: equipment?.shellTminUnit || '',
      headTmin: equipment?.headTmin || 0,
      headTminUnit: equipment?.headTminUnit || ''
    };
  }, [appliesToDataEquipmentId, equipmentId]);

  const { listOfCML, cmlListMap, listOfCMLNumber, selectedUnit } = useMemo(() => {
    let listOfCML = [...(getValues(`${name}.cml`) || [])];
    let selectedUnit = getValues(`${name}.selectedUnit`) || 'in';

    let cmlNumbersLists = [];
    listOfCML = listOfCML.map((cml) => {
      cmlNumbersLists.push({
        id: cml.id,
        cmlNumber: cml.cmlNumber
      });

      return {
        ...cml,
        title: `CML ${cml.cmlNumber}`,
        titleFirstCHild: picklistsMap[cml.component]?.value || '',
        subTitle: cml?.isAddReading
          ? 'Add reading'
          : cml?.isDone
          ? 'Done'
          : cml?.isSkipped
          ? 'Skipped'
          : '',
        dotColor: cml?.isSkipped ? '#CBD2D9' : cml?.isDone ? '#34C369' : '#E02B2B'
      };
    });

    listOfCML = sortCMLList({ list: listOfCML });

    const cmlListMap = listOfCML?.reduce((acc, curr) => {
      acc[curr.id] = { ...curr };
      return acc;
    }, {});

    return { cmlListMap, listOfCML, listOfCMLNumber: cmlNumbersLists, selectedUnit };
  }, [getValues(`${name}.cml`), picklistsMap]);

  const clickAddNewCMLEvent = () => {
    setOpenAddNewCMLModal(true);
  };

  const addNewCMLDataEvent = async ({
    data,
    isDone = false,
    isSkipped = false,
    isAddReading = false
  }) => {
    let copyDataList = [...(getValues(`${name}.cml`) || [])];

    const dataPoints = [
      {
        Id: 'Component',
        key: 'component',
        value: picklistsMap[data?.component]?.value || '',
        title: 'Component'
      },
      {
        Id: 'Location',
        key: 'location',
        value: picklistsMap[data?.location]?.value || '',
        title: 'Location'
      },
      {
        Id: 'Orientation',
        key: 'orientation',
        value: picklistsMap[data?.orientation]?.value || '',
        title: 'Orientation'
      },
      {
        Id: 'Material',
        key: 'material',
        value: data?.material ? picklistsMap[data?.material]?.value || '' : '',
        title: 'Material'
      }
    ];

    let newData = {
      ...data,
      lastMinReading: '',
      lastAvgReading: '',
      errorExists: isSkipped || isDone ? 'No' : isAddReading ? '' : '',
      isDone,
      isSkipped,
      isAddReading,
      dataPoints
    };

    let copyFullCMLData = getValues(name) || {};
    setValue(
      baseName,
      {
        [fieldId]: {
          ...copyFullCMLData,
          cml: [...copyDataList, { ...newData }],
          selectedUnit: data?.selectedLengthUnit
        }
      },
      {
        shouldDirty: true
      }
    );

    setNewCMLAdded(true);
    setTimeout(() => {
      setNewCMLAdded(false);
    }, 3000);

    return true;
  };

  const updateCMLDataEvent = async ({ data, isSkipped = false }) => {
    let copyDataList = [...(getValues(`${name}.cml`) || [])];

    const dataPoints = [
      {
        Id: 'Component',
        key: 'component',
        value: picklistsMap[data?.component]?.value || '',
        title: 'Component'
      },
      {
        Id: 'Location',
        key: 'location',
        value: picklistsMap[data?.location]?.value || '',
        title: 'Location'
      },
      {
        Id: 'Orientation',
        key: 'orientation',
        value: picklistsMap[data?.orientation]?.value || '',
        title: 'Orientation'
      },
      {
        Id: 'Material',
        key: 'material',
        value: data?.material ? picklistsMap[data?.material]?.value || '' : '',
        title: 'Material'
      }
    ];

    const isDone = isSkipped ? false : true;
    let updateData = {
      ...data,
      errorExists: 'No',
      isDone: isDone,
      isSkipped: isSkipped,
      isAddReading: false,
      dataPoints
    };

    const index = copyDataList.findIndex((x) => x?.id == data?.id);
    if (index > -1) {
      copyDataList[index] = { ...updateData };
    }

    let copyFullCMLData = getValues(name) || {};
    setValue(
      baseName,
      {
        [fieldId]: {
          ...copyFullCMLData,
          cml: copyDataList,
          selectedUnit: data?.selectedLengthUnit
        }
      },
      {
        shouldDirty: true
      }
    );

    setEditCMLId(null);
    setOpenAddNewCMLModal(false);

    if (isDone) {
      setNewReadingAdded(true);
      setTimeout(() => {
        setNewReadingAdded(false);
      }, 3000);
    } else if (isSkipped) {
      setNewCMLAdded(true);
      setTimeout(() => {
        setNewCMLAdded(false);
      }, 3000);
    }

    return true;
  };

  const deleteSelectedCML = ({ id }) => {
    let copyDataList = [...(getValues(`${name}.cml`) || [])];

    const filtersList = copyDataList.filter((x) => x?.id !== id);

    let copyFullCMLData = getValues(name) || {};
    setValue(
      baseName,
      {
        [fieldId]: {
          ...copyFullCMLData,
          cml: filtersList
        }
      },
      {
        shouldDirty: true
      }
    );

    setEditCMLId(null);
    setOpenAddNewCMLModal(false);
  };
  const { width } = useWindowDimensions();
  useEffect(() => {
    const formWrapperWIdth = document.getElementById('formWrapperContainer')?.clientWidth || 0;

    const headerWIdth =
      width > 500 ? Math.floor(formWrapperWIdth / 2) - 32 - 16 : formWrapperWIdth - 32;
    setHeaderWidth(headerWIdth);
  }, [
    document.getElementById('cmlContainer'),
    document.getElementById('formWrapperContainer'),
    width
  ]);

  return (
    <div id="cmlContainer">
      <SectionContainer text={'CMLs'} subSection headerClassName={`${style.utSectionOfPhotos}`}>
        <div className={style.cmlContainer}>
          <p className={style.addPSVs} onClick={clickAddNewCMLEvent}>
            <Icon name={'add-icon'} style={{ marginRight: '4px' }} />
            Add new
          </p>

          <div className={`${style.grid2} pt-4`}>
            {listOfCML?.map((cml, index) => (
              <div
                key={cml.id}
                id={cml?.id}
                style={{
                  width: `100%`,
                  minWidth: `${headerWidth ? headerWidth : 450}px`
                }}
              >
                <Collapseable
                  title={cml.title}
                  titleFirstCHild={cml.titleFirstCHild}
                  subTitle={cml?.subTitle}
                  subTitleClass={style.psvsSubTitle}
                  subTitleErrorClass={style.psvsSubTitleError}
                  dot
                  dotColor={cml.dotColor}
                  noAction={true}
                  openCollapse={true}
                  error={Object?.keys(errors)[0]?.includes('ut information') && cml.isAddReading}
                >
                  <>
                    <DataSnippets
                      subtitle={picklistsMap[cml?.component]?.value}
                      dataPoints={cml?.dataPoints}
                      subTitleClassName={style.subTitleClassName}
                      noShowTitle
                    />
                    <div className={` ${style.action} `}>
                      <button
                        type="button"
                        onClick={() => {
                          setEditCMLId(cml?.id);
                          setOpenAddNewCMLModal((prev) => !prev);
                        }}
                      >
                        <span className={style.itemCenter}>
                          {' '}
                          <Icon name={'activity'} /> {cml?.isDone ? 'Edit' : 'ADD'} Reading
                        </span>
                      </button>
                    </div>
                  </>
                </Collapseable>
              </div>
            ))}
          </div>
        </div>
      </SectionContainer>

      {openAddNewCMlModal && (
        <AddEditCMLModal
          open={openAddNewCMlModal}
          title={editCMLId ? 'Add Reading' : 'Add CML'}
          closed={(value) => {
            setEditCMLId(null);
            setOpenAddNewCMLModal(value);
          }}
          dropdownMenuMapList={dropdownMenuMapList}
          isCML={true}
          isReading={editCMLId}
          listOfCMLNumber={listOfCMLNumber}
          cachedData={editCMLId ? cmlListMap[editCMLId] || false : false}
          addNewCMLDataEvent={addNewCMLDataEvent}
          updateCMLDataEvent={updateCMLDataEvent}
          deleteSelectedCML={deleteSelectedCML}
          shellTminAndHeadTminValue={shellTminAndHeadTminValue}
          picklistsMap={picklistsMap}
          persistUnit={selectedUnit}
        />
      )}

      {(newCMLAdded || newReadingAdded) && (
        <>
          <DisabledLocation
            className={style.toastStyles}
            text={newCMLAdded ? 'CML saved' : newReadingAdded ? 'Readings saved' : 'CML saved'}
            showLocationDisabledInfo={true}
          />
        </>
      )}
    </div>
  );
};

export default CMLSubsection;
